import { useEffect, useMemo, useState } from "react";
import { PaginationBar } from "@/components/PaginationBar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  useGrowthAttributionJobsPage,
  useGrowthAttributionSources,
  useGrowthCallCenterReps,
  useGrowthCampaigns,
  useGrowthMarketingSummary,
} from "@/hooks/useGrowthChannelsServerPaging";
import type {
  GrowthAttributionJobRow,
  GrowthAttributionSourceRow,
  GrowthCallCenterRepRow,
  GrowthCampaignRow,
} from "@/services/growth-channels-page.service";
import { cn } from "@/lib/utils";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  BarChart3,
  DollarSign,
  Loader2,
  Megaphone,
  Phone,
  Search,
  Target,
  Trophy,
  Zap,
} from "lucide-react";

type Section = "attribution" | "campaigns" | "callcenter";
type SortDir = "asc" | "desc";

interface Props {
  section: Section;
}

const fmtCurrency = (value: number) => {
  if (!Number.isFinite(value) || value <= 0) return "$0";
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 10_000) return `$${Math.round(value / 1000)}k`;
  if (value >= 1000) return `$${(value / 1000).toFixed(1)}k`;
  return `$${Math.round(value).toLocaleString()}`;
};
const fmtWholeCurrency = (value: number) => Number.isFinite(value) && value > 0 ? `$${Math.round(value).toLocaleString()}` : "—";
const fmtPct = (value: number) => `${Math.round(Number.isFinite(value) ? value : 0)}%`;
const fmtSpeed = (seconds: number) => {
  if (!Number.isFinite(seconds) || seconds <= 0) return "—";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${(seconds / 3600).toFixed(1)}h`;
};
const fmtDate = (value: string) => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

function uniqueSorted(values: Array<string | null | undefined>) {
  return Array.from(new Set(values.map((value) => String(value ?? "").trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b));
}

function pageControls(page: number, pageSize: number, totalItems: number, setPage: (page: number) => void, setPageSize: (size: number) => void) {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  return {
    page,
    totalPages,
    pageStart: totalItems === 0 ? 0 : page * pageSize + 1,
    pageEnd: Math.min(totalItems, (page + 1) * pageSize),
    totalItems,
    hasNext: page + 1 < totalPages,
    hasPrev: page > 0,
    next: () => setPage(Math.min(totalPages - 1, page + 1)),
    prev: () => setPage(Math.max(0, page - 1)),
    pageSize,
    setPageSize: (size: number) => {
      setPage(0);
      setPageSize(size);
    },
  };
}

export default function GrowthMarketingAttributionServer({ section }: Props) {
  const showAttribution = section === "attribution";
  const showCampaigns = section === "campaigns";
  const showCallCenter = section === "callcenter";
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [channelFilter, setChannelFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [qualityFilter, setQualityFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(15);
  const [jobSort, setJobSort] = useState<{ key: string; dir: SortDir }>({ key: "value", dir: "desc" });

  const summary = useGrowthMarketingSummary({ enabled: true });
  const sources = useGrowthAttributionSources({ enabled: showAttribution });
  const jobsPage = useGrowthAttributionJobsPage({
    enabled: showAttribution,
    page,
    pageSize,
    search,
    source: sourceFilter,
    channel: channelFilter,
    status: statusFilter,
    quality: qualityFilter,
    sortKey: jobSort.key,
    sortDir: jobSort.dir,
  });
  const campaigns = useGrowthCampaigns({ enabled: showCampaigns });
  const reps = useGrowthCallCenterReps({ enabled: showCallCenter });

  useEffect(() => {
    setPage(0);
  }, [search, sourceFilter, channelFilter, statusFilter, qualityFilter, jobSort.key, jobSort.dir]);

  const sourceOptions = useMemo(() => uniqueSorted(sources.data.map((row) => row.source)), [sources.data]);
  const channelOptions = useMemo(() => uniqueSorted(sources.data.map((row) => row.channelName)), [sources.data]);

  const pagination = pageControls(page, pageSize, jobsPage.count, setPage, setPageSize);
  const isLoading = summary.loading || sources.loading || jobsPage.loading || campaigns.loading || reps.loading;

  return (
    <div className="space-y-5" data-growth-server-section={section}>
      <MetricStrip section={section} summary={summary.data} loading={summary.loading} />
      {isLoading && <LoadingLine />}
      {showAttribution && (
        <AttributionSection
          rows={sources.data}
          jobs={jobsPage.data}
          jobsCount={jobsPage.count}
          search={search}
          setSearch={setSearch}
          sourceFilter={sourceFilter}
          setSourceFilter={setSourceFilter}
          channelFilter={channelFilter}
          setChannelFilter={setChannelFilter}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          qualityFilter={qualityFilter}
          setQualityFilter={setQualityFilter}
          sourceOptions={sourceOptions}
          channelOptions={channelOptions}
          jobSort={jobSort}
          setJobSort={setJobSort}
          pagination={pagination}
          loading={jobsPage.loading || sources.loading}
        />
      )}
      {showCampaigns && <CampaignSection rows={campaigns.data} loading={campaigns.loading} />}
      {showCallCenter && <CallCenterSection rows={reps.data} loading={reps.loading} />}
    </div>
  );
}

function LoadingLine() {
  return (
    <div className="inline-flex items-center gap-2 text-[12px] text-muted-foreground">
      <Loader2 className="h-3.5 w-3.5 animate-spin" />
      Refreshing growth-channel view data
    </div>
  );
}

function MetricStrip({ section, summary, loading }: { section: Section; summary: ReturnType<typeof useGrowthMarketingSummary>["data"]; loading: boolean }) {
  const data = summary ?? {
    jobsAttributed: 0,
    leadSources: 0,
    attributedRevenue: 0,
    leadSpend: 0,
    netJobs: 0,
    closedCount: 0,
    noShows: 0,
    roas: 0,
    cpaNet: 0,
    avgDealSize: 0,
    costPerAppt: 0,
    revenuePerAppt: 0,
    showRate: 0,
    closeRate: 0,
    avgSpeedSec: 0,
  };
  const metrics = section === "callcenter"
    ? [
        { label: "Appointments", value: String(data.jobsAttributed), icon: Phone, sub: `${data.noShows} no-show` },
        { label: "Show rate", value: fmtPct(data.showRate), icon: Target, sub: `${fmtPct(data.closeRate)} close` },
        { label: "Avg speed", value: fmtSpeed(data.avgSpeedSec), icon: Zap, sub: "first response" },
        { label: "Cost / appt", value: fmtWholeCurrency(data.costPerAppt), icon: DollarSign, sub: `${fmtCurrency(data.revenuePerAppt)} rev/appt` },
        { label: "Revenue", value: fmtCurrency(data.attributedRevenue), icon: Trophy, sub: `${data.closedCount} closed` },
      ]
    : section === "campaigns"
      ? [
          { label: "Sources", value: String(data.leadSources), icon: Megaphone, sub: "campaign inputs" },
          { label: "Revenue", value: fmtCurrency(data.attributedRevenue), icon: Trophy, sub: `${data.netJobs} net jobs` },
          { label: "Lead spend", value: fmtCurrency(data.leadSpend), icon: DollarSign, sub: "from source costs" },
          { label: "Net CPA", value: fmtWholeCurrency(data.cpaNet), icon: Target, sub: "closed net" },
          { label: "ROAS", value: `${data.roas.toFixed(1)}x`, icon: BarChart3, sub: `${fmtCurrency(data.avgDealSize)} avg deal` },
        ]
      : [
          { label: "Jobs attributed", value: String(data.jobsAttributed), icon: Target, sub: `${data.leadSources} sources` },
          { label: "Revenue", value: fmtCurrency(data.attributedRevenue), icon: Trophy, sub: `${data.netJobs} net jobs` },
          { label: "Lead spend", value: fmtCurrency(data.leadSpend), icon: DollarSign, sub: "source costs" },
          { label: "Net CPA", value: fmtWholeCurrency(data.cpaNet), icon: Target, sub: "cost / net job" },
          { label: "ROAS", value: `${data.roas.toFixed(1)}x`, icon: BarChart3, sub: loading ? "loading" : "revenue / spend" },
        ];

  return (
    <dl className="grid grid-cols-2 sm:grid-cols-5 gap-px overflow-hidden rounded-sm border border-border/60 bg-border/60">
      {metrics.map((metric) => {
        const Icon = metric.icon;
        return (
          <div key={metric.label} className="bg-background px-4 py-3">
            <dt className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              <Icon className="h-3 w-3" />
              {metric.label}
            </dt>
            <dd className="mt-1 text-xl font-semibold tracking-tight text-foreground tabular-nums">{metric.value}</dd>
            <p className="mt-0.5 text-[11px] text-muted-foreground">{metric.sub}</p>
          </div>
        );
      })}
    </dl>
  );
}

function AttributionSection({
  rows,
  jobs,
  jobsCount,
  search,
  setSearch,
  sourceFilter,
  setSourceFilter,
  channelFilter,
  setChannelFilter,
  statusFilter,
  setStatusFilter,
  qualityFilter,
  setQualityFilter,
  sourceOptions,
  channelOptions,
  jobSort,
  setJobSort,
  pagination,
  loading,
}: {
  rows: GrowthAttributionSourceRow[];
  jobs: GrowthAttributionJobRow[];
  jobsCount: number;
  search: string;
  setSearch: (value: string) => void;
  sourceFilter: string;
  setSourceFilter: (value: string) => void;
  channelFilter: string;
  setChannelFilter: (value: string) => void;
  statusFilter: string;
  setStatusFilter: (value: string) => void;
  qualityFilter: string;
  setQualityFilter: (value: string) => void;
  sourceOptions: string[];
  channelOptions: string[];
  jobSort: { key: string; dir: SortDir };
  setJobSort: (value: { key: string; dir: SortDir }) => void;
  pagination: ReturnType<typeof pageControls>;
  loading: boolean;
}) {
  const setSort = (key: string) => {
    setJobSort(jobSort.key === key ? { key, dir: jobSort.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" });
  };

  return (
    <div className="space-y-5">
      <section>
        <div className="flex items-center justify-between gap-3 pb-2 border-b border-border">
          <div>
            <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-semibold">Source rollup</p>
            <h3 className="text-sm font-semibold text-foreground">Lead source to revenue path</h3>
          </div>
          <span className="text-[11px] text-muted-foreground">{rows.length} sources</span>
        </div>
        <div className="grid grid-cols-[minmax(0,1.5fr)_130px_70px_70px_90px_90px_90px] items-center gap-3 px-4 py-2.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground border-b border-border">
          <span>Source</span>
          <span>Channel</span>
          <span className="text-right">Leads</span>
          <span className="text-right">Net</span>
          <span className="text-right">Revenue</span>
          <span className="text-right">Net CPA</span>
          <span className="text-right">Quality</span>
        </div>
        <ul className="divide-y divide-border border-b border-border">
          {rows.length === 0 && <EmptyRow text={loading ? "Loading source attribution..." : "No attribution sources found."} />}
          {rows.map((row) => <SourceRollupRow key={row.source} row={row} />)}
        </ul>
      </section>

      <section>
        <div className="flex flex-wrap items-center gap-3 py-3">
          <div className="relative min-w-[220px] flex-1 max-w-sm">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search job, customer, source..." className="h-8 pl-8 text-[13px]" />
          </div>
          <SmallSelect value={sourceFilter} onChange={setSourceFilter} placeholder="Source" options={sourceOptions} allLabel="All sources" />
          <SmallSelect value={channelFilter} onChange={setChannelFilter} placeholder="Channel" options={channelOptions} allLabel="All channels" />
          <SmallSelect value={statusFilter} onChange={setStatusFilter} placeholder="Outcome" options={["Net", "Pending", "Canceled", "Finance Declined", "—"]} allLabel="All outcomes" />
          <SmallSelect value={qualityFilter} onChange={setQualityFilter} placeholder="Quality" options={["Valid", "Out-of-Area", "Low Quality"]} allLabel="All quality" />
        </div>

        <div className="border border-border/70 rounded-sm overflow-hidden">
          <div className="grid grid-cols-[110px_minmax(0,1.15fr)_130px_120px_90px_82px_90px_100px] items-center gap-3 px-4 py-2.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground border-b border-border bg-muted/20">
            <SortButton label="Job" sortKey="id" state={jobSort} onClick={setSort} />
            <SortButton label="Customer" sortKey="customer" state={jobSort} onClick={setSort} />
            <SortButton label="Source" sortKey="source" state={jobSort} onClick={setSort} />
            <SortButton label="Channel" sortKey="channel" state={jobSort} onClick={setSort} />
            <SortButton label="Score" sortKey="score" state={jobSort} onClick={setSort} align="right" />
            <span className="text-right">Outcome</span>
            <SortButton label="Value" sortKey="value" state={jobSort} onClick={setSort} align="right" />
            <SortButton label="Appt" sortKey="apptDate" state={jobSort} onClick={setSort} align="right" />
          </div>
          <ul className="divide-y divide-border">
            {jobs.length === 0 && <EmptyRow text={loading ? "Loading attributed jobs..." : "No attributed jobs match the filters."} />}
            {jobs.map((job) => <JobAttributionRow key={job.jobId} row={job} />)}
          </ul>
          <PaginationBar {...pagination} />
        </div>
        <p className="mt-2 text-[11px] text-muted-foreground">Showing a server-paged job table: {jobsCount.toLocaleString()} attributed rows available.</p>
      </section>
    </div>
  );
}

function SourceRollupRow({ row }: { row: GrowthAttributionSourceRow }) {
  return (
    <li className="grid grid-cols-[minmax(0,1.5fr)_130px_70px_70px_90px_90px_90px] items-center gap-3 px-4 py-3 text-[13px] hover:bg-muted/30 transition-colors">
      <div className="min-w-0">
        <div className="font-semibold truncate text-foreground">{row.source}</div>
        <div className="text-[11px] text-muted-foreground">{row.issues} issue{row.issues === 1 ? "" : "s"} · {fmtPct(row.netConversion)} net conv</div>
      </div>
      <span className="truncate text-muted-foreground">{row.channelName}</span>
      <span className="text-right tabular-nums text-foreground">{row.leads}</span>
      <span className="text-right tabular-nums text-foreground">{row.netJobs}</span>
      <span className="text-right tabular-nums text-foreground">{fmtCurrency(row.revenue)}</span>
      <span className="text-right tabular-nums text-foreground">{fmtWholeCurrency(row.cpaNet)}</span>
      <span className={cn("text-right tabular-nums font-semibold", row.leadQuality >= 70 ? "text-success" : row.leadQuality >= 50 ? "text-warning" : "text-destructive")}>{fmtPct(row.leadQuality)}</span>
    </li>
  );
}

function JobAttributionRow({ row }: { row: GrowthAttributionJobRow }) {
  return (
    <li className="grid grid-cols-[110px_minmax(0,1.15fr)_130px_120px_90px_82px_90px_100px] items-center gap-3 px-4 py-3 text-[13px] hover:bg-muted/30 transition-colors">
      <div className="min-w-0">
        <div className="font-semibold text-foreground truncate">{row.jobNumber || row.jobId}</div>
        <div className="text-[11px] text-muted-foreground truncate">{row.salesRep || "Unassigned"}</div>
      </div>
      <div className="min-w-0">
        <div className="font-medium text-foreground truncate">{row.customerName}</div>
        <div className="text-[11px] text-muted-foreground truncate">{row.branch || "No branch"} · {row.region || "No region"}</div>
      </div>
      <span className="truncate text-muted-foreground">{row.leadSource}</span>
      <span className="truncate text-muted-foreground">{row.channelName}</span>
      <span className="text-right tabular-nums text-foreground">{Math.round(row.bookingScore)}</span>
      <span className="flex justify-end"><OutcomeBadge value={row.outcome} /></span>
      <span className="text-right tabular-nums text-foreground">{fmtCurrency(row.jobValue)}</span>
      <span className="text-right text-muted-foreground text-[12px]">{fmtDate(row.appointmentTime)}</span>
    </li>
  );
}

function CampaignSection({ rows, loading }: { rows: GrowthCampaignRow[]; loading: boolean }) {
  return (
    <section>
      <div className="flex items-center justify-between gap-3 pb-2 border-b border-border">
        <div>
          <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-semibold">Campaigns</p>
          <h3 className="text-sm font-semibold text-foreground">ROI and pacing by campaign</h3>
        </div>
        <span className="text-[11px] text-muted-foreground">{rows.length} campaigns</span>
      </div>
      <div className="grid grid-cols-[minmax(0,1.4fr)_130px_70px_90px_90px_90px_100px] items-center gap-3 px-4 py-2.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground border-b border-border">
        <span>Campaign</span>
        <span>Channel</span>
        <span className="text-right">Jobs</span>
        <span className="text-right">Net</span>
        <span className="text-right">Revenue</span>
        <span className="text-right">CPA</span>
        <span className="text-right">Decision</span>
      </div>
      <ul className="divide-y divide-border border-b border-border">
        {rows.length === 0 && <EmptyRow text={loading ? "Loading campaigns..." : "No campaign rows found."} />}
        {rows.map((row) => <CampaignRow key={`${row.campaign}:${row.channel}`} row={row} />)}
      </ul>
    </section>
  );
}

function CampaignRow({ row }: { row: GrowthCampaignRow }) {
  const decision = row.netJobs >= 3 && row.cpa > 0 && row.cpa <= 500 ? "Scale" : row.netConversion >= 20 ? "Hold" : "Review";
  return (
    <li className="grid grid-cols-[minmax(0,1.4fr)_130px_70px_90px_90px_90px_100px] items-center gap-3 px-4 py-3 text-[13px] hover:bg-muted/30 transition-colors">
      <div className="min-w-0">
        <div className="font-semibold text-foreground truncate">{row.campaign}</div>
        <div className="text-[11px] text-muted-foreground truncate">{row.sources || "No source mix"}</div>
      </div>
      <span className="truncate text-muted-foreground">{row.channel || "Unattributed"}</span>
      <span className="text-right tabular-nums text-foreground">{row.jobs}</span>
      <span className="text-right tabular-nums text-foreground">{row.netJobs}</span>
      <span className="text-right tabular-nums text-foreground">{fmtCurrency(row.revenue)}</span>
      <span className="text-right tabular-nums text-foreground">{fmtWholeCurrency(row.cpa)}</span>
      <span className="flex justify-end"><DecisionBadge value={decision} /></span>
    </li>
  );
}

function CallCenterSection({ rows, loading }: { rows: GrowthCallCenterRepRow[]; loading: boolean }) {
  return (
    <section>
      <div className="flex items-center justify-between gap-3 pb-2 border-b border-border">
        <div>
          <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-semibold">Call center</p>
          <h3 className="text-sm font-semibold text-foreground">Speed, show, close, and revenue by rep</h3>
        </div>
        <span className="text-[11px] text-muted-foreground">{rows.length} reps</span>
      </div>
      <div className="grid grid-cols-[minmax(0,1.2fr)_80px_80px_90px_90px_90px_90px_90px] items-center gap-3 px-4 py-2.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground border-b border-border">
        <span>Rep</span>
        <span className="text-right">Appts</span>
        <span className="text-right">Closed</span>
        <span className="text-right">Show</span>
        <span className="text-right">Close</span>
        <span className="text-right">Speed</span>
        <span className="text-right">Cost/appt</span>
        <span className="text-right">Revenue</span>
      </div>
      <ul className="divide-y divide-border border-b border-border">
        {rows.length === 0 && <EmptyRow text={loading ? "Loading call center rows..." : "No call center rows found."} />}
        {rows.map((row) => <CallCenterRow key={row.rep} row={row} />)}
      </ul>
    </section>
  );
}

function CallCenterRow({ row }: { row: GrowthCallCenterRepRow }) {
  return (
    <li className="grid grid-cols-[minmax(0,1.2fr)_80px_80px_90px_90px_90px_90px_90px] items-center gap-3 px-4 py-3 text-[13px] hover:bg-muted/30 transition-colors">
      <div className="min-w-0">
        <div className="font-semibold text-foreground truncate">{row.rep}</div>
        <div className="text-[11px] text-muted-foreground truncate">{row.noShows} no-show · {fmtPct(row.highFitConversion)} high-fit</div>
      </div>
      <span className="text-right tabular-nums text-foreground">{row.appointments}</span>
      <span className="text-right tabular-nums text-foreground">{row.closed}</span>
      <span className="text-right tabular-nums text-foreground">{fmtPct(row.showRate)}</span>
      <span className="text-right tabular-nums text-foreground">{fmtPct(row.closeRate)}</span>
      <span className="text-right tabular-nums text-muted-foreground">{fmtSpeed(row.speedSec)}</span>
      <span className="text-right tabular-nums text-foreground">{fmtWholeCurrency(row.costPerAppt)}</span>
      <span className="text-right tabular-nums text-foreground">{fmtCurrency(row.revenue)}</span>
    </li>
  );
}

function SmallSelect({ value, onChange, options, allLabel }: { value: string; onChange: (value: string) => void; placeholder: string; options: string[]; allLabel: string }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-8 w-[150px] text-[12px]"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{allLabel}</SelectItem>
        {options.map((option) => <SelectItem key={option} value={option}>{option}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function SortButton({ label, sortKey, state, onClick, align = "left" }: { label: string; sortKey: string; state: { key: string; dir: SortDir }; onClick: (key: string) => void; align?: "left" | "right" }) {
  const active = state.key === sortKey;
  const Icon = !active ? ArrowUpDown : state.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <button type="button" onClick={() => onClick(sortKey)} className={cn("inline-flex items-center gap-1 hover:text-foreground", align === "right" && "justify-end text-right")}> 
      {label}
      <Icon className="h-3 w-3" />
    </button>
  );
}

function OutcomeBadge({ value }: { value: GrowthAttributionJobRow["outcome"] }) {
  const tone = value === "Net" ? "bg-success/10 text-success border-success/20"
    : value === "Pending" ? "bg-warning/10 text-warning border-warning/20"
      : value === "Canceled" || value === "Finance Declined" ? "bg-destructive/10 text-destructive border-destructive/20"
        : "bg-muted text-muted-foreground border-border";
  return <Badge variant="outline" className={cn("text-[10px]", tone)}>{value}</Badge>;
}

function DecisionBadge({ value }: { value: string }) {
  const tone = value === "Scale" ? "bg-success/10 text-success border-success/20"
    : value === "Hold" ? "bg-primary/10 text-primary border-primary/20"
      : "bg-warning/10 text-warning border-warning/20";
  return <Badge variant="outline" className={cn("text-[10px]", tone)}>{value}</Badge>;
}

function EmptyRow({ text }: { text: string }) {
  return <li className="px-4 py-8 text-center text-[13px] text-muted-foreground">{text}</li>;
}
